// ========================================
// COMPONENTE DE GERENCIAMENTO DE CLIENTES
// src/components/Clientes.jsx
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  MapPin,
  Phone,
  Mail,
  AlertTriangle,
  Eye,
  Shield,
  ChevronDown,
  ChevronUp,
  Filter,
  User,
  Calendar,
  DollarSign,
  Scale,
  X,
  Loader2,
  Edit3,
  Save,
  Ban,
  UserCheck,
  RefreshCw,
  Trash2,
  Plus,
  Copy,
  ClipboardCheck,
  Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { clienteAPI, usuarioAPI } from '../services/api';
import { useModal } from '../contexts/ModalContext';
import Loading from './Loading';
import { 
  CLIENTE_STATUS, 
  CLIENTE_STATUS_CONFIG, 
  getClienteStatusConfig,
  clientePodeFazerPedido 
} from '../config/clienteStatusConfig';

// ========================================
// COMPONENTE PRINCIPAL: CLIENTES
// ========================================
const Clientes = ({ secaoInicial = 'consulta' }) => {
  const { usuario, isAdminOuDiretoria, temPermissao } = useAuth();
  const [secaoAtiva, setSecaoAtiva] = useState(secaoInicial);

  // Atualiza seção quando prop muda
  useEffect(() => {
    setSecaoAtiva(secaoInicial);
  }, [secaoInicial]);
  
  // Verificar se pode acessar aprovações
  const podeAprovarClientes = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase();
    return dep === 'admin' || dep === 'administrativo' || dep === 'diretoria' || 
           usuario.permissoes?.pode_aprovar_clientes === true;
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#f3f4f6' }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Conteúdo baseado na seção ativa */}
        {secaoAtiva === 'consulta' && <ConsultaClientes />}
        {secaoAtiva === 'aprovacoes' && podeAprovarClientes() && <AprovacoesClientes />}
        
        {/* Se não tem permissão para aprovações e tentou acessar */}
        {secaoAtiva === 'aprovacoes' && !podeAprovarClientes() && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center max-w-2xl mx-auto mt-10">
            <Shield size={64} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-red-800 mb-2">Acesso Negado</h2>
            <p className="text-red-700">Você não tem permissão para acessar as aprovações de clientes.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// ABA: CONSULTA DE CLIENTES
// ========================================
const ConsultaClientes = () => {
  const { usuario } = useAuth();
  const { showModal } = useModal();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroMunicipio, setFiltroMunicipio] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');
  const [clienteExpandido, setClienteExpandido] = useState(null);
  const [editandoObservacao, setEditandoObservacao] = useState(null); // id do cliente sendo editado
  const [textoObservacao, setTextoObservacao] = useState('');
  const [cnpjCopiado, setCnpjCopiado] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [quantidadeVisivel, setQuantidadeVisivel] = useState(10);
  const [modalEdicao, setModalEdicao] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  const [novoCliente, setNovoCliente] = useState({
    nome: '', cnpj: '', endereco: '', bairro: '', cidade: '', estado: 'Parana', telefone: '', email: '', rede: '', data_constituicao_nascimento: ''
  });
  const [erroValidacao, setErroValidacao] = useState({ cnpj: '', telefone: '' });
  const [verificandoCnpj, setVerificandoCnpj] = useState(false);

  // Lista de usuários ativos para filtro de responsável
  const [usuariosParaFiltro, setUsuariosParaFiltro] = useState([]);

  // Helper para obter nome formatado do usuário
  const obterNomeUsuario = () => {
    const username = usuario?.username_ldap || usuario?.nome;
    if (username === 'admin_sistema') {
      return 'Administrador do Sistema';
    }
    return username;
  };

  // Helper para converter username_ldap em nome completo
  const obterNomeResponsavel = (usernameOuNome) => {
    if (!usernameOuNome) return '-';
    // Procurar nos usuários carregados
    const usuarioEncontrado = usuariosParaFiltro.find(u => 
      u.username_ldap === usernameOuNome || u.nome_completo === usernameOuNome
    );
    if (usuarioEncontrado) {
      return usuarioEncontrado.nome_completo || usuarioEncontrado.username_ldap;
    }
    // Se não encontrou, retorna o valor original
    return usernameOuNome;
  };

  // Listas únicas para os filtros (extraídas dos clientes)
  const [estadosDisponiveis, setEstadosDisponiveis] = useState([]);
  const [municipiosDisponiveis, setMunicipiosDisponiveis] = useState([]);
  
  // Situações disponíveis para filtro
  const situacoesDisponiveis = [
    { value: 'ATIVO', label: 'Cliente Ativo' },
    { value: 'INATIVO', label: 'Cliente Inativo' },
    { value: 'CADASTRADO', label: 'Cliente Cadastrado' },
    { value: 'APROVADO', label: 'Cadastro Aprovado' },
    { value: 'EM_ANALISE', label: 'Cadastro em Análise' },
    { value: 'PENDENTE', label: 'Cadastro em Análise' },
    { value: 'REJEITADO', label: 'Cadastro Rejeitado' },
    { value: 'EXCLUIDO', label: 'Cliente Excluído' }
  ];

  useEffect(() => {
    carregarClientes();
    // Carregar usuários ativos para o filtro de responsável
    const carregarUsuariosParaFiltro = async () => {
      try {
        const usuarios = await usuarioAPI.listarAtivos();
        setUsuariosParaFiltro(usuarios);
      } catch (error) {
        console.error('Erro ao carregar usuários para filtro:', error);
      }
    };
    carregarUsuariosParaFiltro();
  }, []);

  // Atualizar listas de filtros quando clientes mudam
  useEffect(() => {
    if (clientes.length > 0) {
      // Estados únicos
      const estados = [...new Set(clientes.map(c => c.estado).filter(Boolean))].sort();
      setEstadosDisponiveis(estados);
    }
  }, [clientes]);

  // Atualizar municípios quando estado muda
  useEffect(() => {
    if (filtroEstado && clientes.length > 0) {
      const municipios = [...new Set(
        clientes
          .filter(c => c.estado === filtroEstado)
          .map(c => c.cidade)
          .filter(Boolean)
      )].sort();
      setMunicipiosDisponiveis(municipios);
    } else {
      // Se não tem estado selecionado, mostrar todos os municípios
      const municipios = [...new Set(clientes.map(c => c.cidade).filter(Boolean))].sort();
      setMunicipiosDisponiveis(municipios);
    }
    // Limpar município selecionado quando estado muda
    setFiltroMunicipio('');
  }, [filtroEstado, clientes]);

  const carregarClientes = async () => {
    try {
      setLoading(true);
      const data = await clienteAPI.listarPorVendedor(usuario?.username_ldap);
      setClientes(data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Não foi possível carregar a lista de clientes.',
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FUNÇÕES DE MÁSCARA E VALIDAÇÃO PARA CADASTRO
  // ========================================
  
  // Máscara de CPF: 000.000.000-00
  const mascaraCPF = (valor) => {
    return valor
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  // Máscara de CNPJ: 00.000.000/0000-00
  const mascaraCNPJ = (valor) => {
    return valor
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  // Máscara automática CPF/CNPJ
  const mascaraCpfCnpj = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length <= 11) {
      return mascaraCPF(valor);
    } else {
      return mascaraCNPJ(valor);
    }
  };

  // Máscara de Telefone: (99) 9999-9999 ou (99) 99999-9999
  const mascaraTelefone = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length <= 10) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
    } else {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
    }
  };

  // Validar formato de telefone
  const validarTelefone = (telefone) => {
    const apenasNumeros = telefone.replace(/\D/g, '');
    return apenasNumeros.length >= 10 && apenasNumeros.length <= 11;
  };

  // Validar formato de CPF/CNPJ
  const validarCpfCnpj = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    return apenasNumeros.length === 11 || apenasNumeros.length === 14;
  };

  // Handler para mudança de CPF/CNPJ com validação de duplicidade
  const handleCnpjChange = async (valor) => {
    const valorFormatado = mascaraCpfCnpj(valor);
    setNovoCliente({ ...novoCliente, cnpj: valorFormatado });
    setErroValidacao(prev => ({ ...prev, cnpj: '' }));

    const apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length === 11 || apenasNumeros.length === 14) {
      setVerificandoCnpj(true);
      try {
        const clienteExistente = await clienteAPI.buscarPorCNPJ(valorFormatado);
        if (clienteExistente) {
          setErroValidacao(prev => ({ ...prev, cnpj: 'Cliente já cadastrado com este CPF/CNPJ' }));
        }
      } catch (error) {
        // Se não encontrou, é ok
      } finally {
        setVerificandoCnpj(false);
      }
    }
  };

  // Handler para mudança de telefone com máscara
  const handleTelefoneChange = (valor) => {
    const valorFormatado = mascaraTelefone(valor);
    setNovoCliente({ ...novoCliente, telefone: valorFormatado });
    
    const apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length > 0 && apenasNumeros.length < 10) {
      setErroValidacao(prev => ({ ...prev, telefone: 'Telefone inválido' }));
    } else {
      setErroValidacao(prev => ({ ...prev, telefone: '' }));
    }
  };

  // Salvar novo cliente
  const salvarNovoCliente = async () => {
    if (!novoCliente.nome || !novoCliente.cnpj) { 
      showModal({ type: 'warning', title: 'Campos obrigatórios', message: 'Preencha Nome e CPF/CNPJ.', confirmText: 'OK', showCancel: false }); 
      return; 
    }

    if (!novoCliente.telefone || !validarTelefone(novoCliente.telefone)) {
      showModal({ type: 'warning', title: 'Telefone inválido', message: 'Informe um telefone válido no formato (99) 9999-9999 ou (99) 99999-9999.', confirmText: 'OK', showCancel: false });
      return;
    }

    if (!validarCpfCnpj(novoCliente.cnpj)) {
      showModal({ type: 'warning', title: 'CPF/CNPJ inválido', message: 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.', confirmText: 'OK', showCancel: false });
      return;
    }

    if (erroValidacao.cnpj) {
      showModal({ type: 'error', title: 'Cliente já cadastrado', message: 'Já existe um cliente cadastrado com este CPF/CNPJ. Utilize a busca para encontrá-lo.', confirmText: 'OK', showCancel: false });
      return;
    }

    try {
      const resultado = await clienteAPI.criar(novoCliente, usuario);
      setMostrarCadastro(false);
      setNovoCliente({ nome: '', cnpj: '', endereco: '', bairro: '', cidade: '', estado: 'Parana', telefone: '', email: '', rede: '', data_constituicao_nascimento: '' });
      setErroValidacao({ cnpj: '', telefone: '' });
      
      // Verificar se foi aprovado automaticamente
      if (resultado?.aprovadoAutomaticamente) {
        showModal({ 
          type: 'success', 
          title: 'Cliente Aprovado Automaticamente!', 
          message: 'O cliente foi cadastrado e aprovado automaticamente com Porte 1 (Limite R$ 5.000,00). Você já pode utilizá-lo para criar pedidos!', 
          confirmText: 'OK', 
          showCancel: false 
        });
      } else {
        showModal({ 
          type: 'success', 
          title: 'Cliente Enviado para Análise', 
          message: 'O cadastro do cliente foi enviado para aprovação. Você será notificado quando o cadastro for aprovado.', 
          confirmText: 'OK', 
          showCancel: false 
        });
      }
      carregarClientes();
    } catch (error) { 
      console.error('Erro ao cadastrar cliente:', error);
      const mensagem = error.code === '23505' ? 'CPF/CNPJ já existe.' : (error.message || error.error || 'Erro ao cadastrar.');
      showModal({ type: 'error', title: 'Erro', message: mensagem, confirmText: 'OK', showCancel: false }); 
    }
  };

  const clientesFiltrados = clientes.filter(cliente => {
    const matchFiltro = filtro === '' || 
      cliente.nome?.toLowerCase().includes(filtro.toLowerCase()) ||
      cliente.cnpj?.includes(filtro) ||
      cliente.cidade?.toLowerCase().includes(filtro.toLowerCase());
    
    const matchEstado = filtroEstado === '' || cliente.estado === filtroEstado;
    const matchMunicipio = filtroMunicipio === '' || cliente.cidade === filtroMunicipio;
    
    // Filtro de responsável - compara com username_ldap ou nome_completo
    const matchResponsavel = filtroResponsavel === '' || 
      cliente.vendedor_responsavel === filtroResponsavel ||
      // Verificar se o filtro selecionado corresponde ao usuário do cliente (para casos antigos com nome completo)
      usuariosParaFiltro.some(u => 
        u.username_ldap === filtroResponsavel && 
        (u.nome_completo === cliente.vendedor_responsavel || u.username_ldap === cliente.vendedor_responsavel)
      );
    
    // Filtro de situação - considera status_calculado ou status_cadastro
    const statusCliente = cliente.status_calculado || cliente.status_cadastro;
    const matchSituacao = filtroSituacao === '' || 
      statusCliente === filtroSituacao || 
      (filtroSituacao === 'EM_ANALISE' && statusCliente === 'PENDENTE');
    
    return matchFiltro && matchEstado && matchMunicipio && matchResponsavel && matchSituacao;
  });

  // Estatísticas
  const totalClientes = clientes.length;

  // Clientes para exibição (limitado)
  const clientesVisiveis = clientesFiltrados.slice(0, quantidadeVisivel);
  const temMaisClientes = clientesFiltrados.length > quantidadeVisivel;

  // Verificar se pode editar porte/limite
  const podeEditarPorteLimite = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase();
    return dep === 'admin' || dep === 'administrativo' || dep === 'diretoria';
  };

  // Verificar se pode editar o NOME do cliente
  // Regra: somente quem tem perfil de aprovação de cadastro de clientes
  const podeEditarNome = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase();
    return dep === 'admin' || dep === 'administrativo' || dep === 'diretoria' ||
           temPermissao('pode_aprovar_clientes');
  };

  // Permissão para editar observação (usa departamento, como as demais verificações)
  const depObs = (usuario?.departamento || '').toLowerCase();
  const podeEditarObservacao = depObs === 'diretoria' || depObs === 'administrativo' || depObs === 'admin';

  const abrirEdicaoObservacao = (cliente, e) => {
    e.stopPropagation();
    setEditandoObservacao(cliente.id);
    setTextoObservacao(cliente.observacao_analise || '');
  };

  const salvarObservacao = async (clienteId, e) => {
    e.stopPropagation();
    try {
      await clienteAPI.atualizarObservacao(clienteId, textoObservacao);
      setClientes(prev => prev.map(c => c.id === clienteId ? { ...c, observacao_analise: textoObservacao } : c));
      setEditandoObservacao(null);
    } catch (error) {
      showModal({ type: 'error', title: 'Erro', message: 'Não foi possível salvar a observação.' });
    }
  };

  const cancelarEdicaoObservacao = (e) => {
    e.stopPropagation();
    setEditandoObservacao(null);
  };

  // Abrir modal de edição
  const abrirEdicao = (cliente, e) => {
    e.stopPropagation();
    setClienteEditando({ ...cliente });
    setModalEdicao(true);
  };

  // Reativar cliente excluído ou rejeitado
  const reativarCliente = async (cliente, e) => {
    e.stopPropagation();
    
    // Verificar permissão
    if (usuario.cargo !== 'Diretoria' && usuario.cargo !== 'Administrativo' && usuario.cargo !== 'Admin') {
      showModal({
        type: 'error',
        title: 'Acesso Negado',
        message: 'Apenas Diretoria, Administrativo ou Admin podem reativar clientes.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    // Confirmar reativação
    const confirmar = await new Promise(resolve => {
      showModal({
        type: 'warning',
        title: 'Confirmar Reativação',
        message: `Deseja realmente reativar o cliente ${cliente.nome}? O status será alterado para "Cadastrado".`,
        confirmText: 'Sim, Reativar',
        cancelText: 'Cancelar',
        showCancel: true,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });

    if (!confirmar) return;

    try {
      await clienteAPI.reativarCliente(cliente.id, {
        reativado_por: obterNomeUsuario(),
        usuario_id_reativou: usuario.id
      });
      
      showModal({
        type: 'success',
        title: 'Sucesso!',
        message: 'Cliente reativado com sucesso. Status alterado para "Cadastrado".',
        confirmText: 'OK',
        showCancel: false
      });
      
      carregarClientes();
    } catch (error) {
      console.error('Erro ao reativar cliente:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Não foi possível reativar o cliente.',
        confirmText: 'OK',
        showCancel: false
      });
    }
  };

  // Salvar edição
  const salvarEdicao = async (dadosAtualizados) => {
    try {
      await clienteAPI.atualizar(clienteEditando.id, dadosAtualizados);
      showModal({
        type: 'success',
        title: 'Sucesso!',
        message: 'Cliente atualizado com sucesso.',
        confirmText: 'OK',
        showCancel: false
      });
      setModalEdicao(false);
      setClienteEditando(null);
      carregarClientes();
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: error?.message || 'Não foi possível atualizar o cliente.',
        confirmText: 'OK',
        showCancel: false
      });
    }
  };

  // Badge de Situação - Usando nova configuração de status
  const getSituacaoBadge = (status) => {
    const config = getClienteStatusConfig(status);
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${config.bgColor} ${config.textColor}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const getPorteBadge = (porte) => {
    if (porte === null || porte === undefined) return null;
    const cores = {
      '0': 'bg-blue-100 text-blue-700',
      '1': 'bg-purple-100 text-purple-700',
      '2': 'bg-indigo-100 text-indigo-700',
      '3': 'bg-green-100 text-green-700'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cores[porte] || 'bg-slate-100 text-slate-700'}`}>
        <Scale size={12} />
        Porte {porte}
      </span>
    );
  };

  const formatarMoeda = (valor) => {
    return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className="space-y-6">
      {/* Header simples estilo GerenciarUsuarios */}
      <div className="animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-default">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Consulta de Clientes</h1>
              <p className="text-xs text-gray-400">Gerencie e visualize todos os clientes cadastrados</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-blue-600">{loading ? '...' : totalClientes}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Cadastrados</p>
          </div>
        </div>
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200"></div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300">
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative group">
            <Search className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-300" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, CNPJ ou cidade..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-300 hover:shadow-sm transition-all duration-300"
            />
          </div>
          
          {/* Botão Cadastrar Novo Cliente */}
          {!mostrarCadastro && (
            <button 
              onClick={() => setMostrarCadastro(true)} 
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-5 py-3 rounded-xl hover:from-emerald-600 hover:to-emerald-700 hover:scale-105 hover:shadow-lg flex items-center gap-2 font-semibold transition-all duration-300 shadow-md group"
            >
              <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
              Cadastrar novo cliente
            </button>
          )}
          
          <button 
            onClick={() => setMostrarFiltros(!mostrarFiltros)} 
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 hover:scale-105 hover:shadow-lg flex items-center gap-2 font-semibold transition-all duration-300 shadow-md group"
          >
            <Filter size={20} className="group-hover:rotate-180 transition-transform duration-500" />
            Filtros
            {mostrarFiltros ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
        
        {mostrarFiltros && (
          <div className="border-t-2 border-gray-100 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filtro de Estado */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                >
                  <option value="">Todos os Estados</option>
                  {estadosDisponiveis.map(estado => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de Município */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Município</label>
                <select
                  value={filtroMunicipio}
                  onChange={(e) => setFiltroMunicipio(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                >
                  <option value="">Todos os Municípios</option>
                  {municipiosDisponiveis.map(municipio => (
                    <option key={municipio} value={municipio}>{municipio}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de Situação */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Situação</label>
                <select
                  value={filtroSituacao}
                  onChange={(e) => setFiltroSituacao(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                >
                  <option value="">Todas as Situações</option>
                  {situacoesDisponiveis.filter((s, i, arr) => 
                    arr.findIndex(x => x.label === s.label) === i
                  ).map(situacao => (
                    <option key={situacao.value} value={situacao.value}>{situacao.label}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de Responsável */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Responsável</label>
                <select
                  value={filtroResponsavel}
                  onChange={(e) => setFiltroResponsavel(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                >
                  <option value="">Todos os Responsáveis</option>
                  {usuariosParaFiltro.map(u => (
                    <option key={u.id} value={u.username_ldap}>
                      {u.nome_completo || u.username_ldap}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contador */}
      <div className="text-sm text-slate-500 font-medium">
        {clientesFiltrados.length} cliente{clientesFiltrados.length !== 1 ? 's' : ''} encontrado{clientesFiltrados.length !== 1 ? 's' : ''}
      </div>

      {/* Formulário de Cadastro de Novo Cliente */}
      {mostrarCadastro && (
        <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-3 rounded-xl shadow-lg hover:scale-110 transition-transform duration-300">
                <Plus size={24} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Novo Cliente</h3>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-5 py-2 rounded-xl font-bold text-sm shadow-lg flex items-center gap-2">
              <CheckCircle size={16} />
              NOVO
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Nome *</label>
              <input 
                type="text" 
                value={novoCliente.nome} 
                onChange={(e) => setNovoCliente({...novoCliente, nome: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300" 
              />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">CPF/CNPJ *</label>
              <input 
                type="text" 
                value={novoCliente.cnpj} 
                onChange={(e) => handleCnpjChange(e.target.value)}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength={18}
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                  erroValidacao.cnpj 
                    ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100' 
                    : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100'
                }`}
              />
              {verificandoCnpj && <p className="text-xs text-blue-500 mt-1 flex items-center gap-1"><Clock size={12} className="animate-spin" /> Verificando...</p>}
              {erroValidacao.cnpj && <p className="text-xs text-red-500 mt-1 font-semibold">{erroValidacao.cnpj}</p>}
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Endereço</label>
              <input 
                type="text" 
                value={novoCliente.endereco} 
                onChange={(e) => setNovoCliente({...novoCliente, endereco: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300" 
              />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Bairro</label>
              <input 
                type="text" 
                value={novoCliente.bairro} 
                onChange={(e) => setNovoCliente({...novoCliente, bairro: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300" 
              />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Cidade</label>
              <input 
                type="text" 
                value={novoCliente.cidade} 
                onChange={(e) => setNovoCliente({...novoCliente, cidade: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300" 
              />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Estado</label>
              <select 
                value={novoCliente.estado} 
                onChange={(e) => setNovoCliente({...novoCliente, estado: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 cursor-pointer transition-all duration-300"
              >
                <option value="Parana">Paraná</option>
                <option value="Santa Catarina">Santa Catarina</option>
              </select>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Telefone *</label>
              <input 
                type="text" 
                value={novoCliente.telefone} 
                onChange={(e) => handleTelefoneChange(e.target.value)}
                placeholder="(99) 99999-9999"
                maxLength={15}
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                  erroValidacao.telefone 
                    ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100' 
                    : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100'
                }`}
              />
              {erroValidacao.telefone && <p className="text-xs text-red-500 mt-1 font-semibold">{erroValidacao.telefone}</p>}
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Email</label>
              <input 
                type="email" 
                value={novoCliente.email} 
                onChange={(e) => setNovoCliente({...novoCliente, email: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300" 
              />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Rede</label>
              <select 
                value={novoCliente.rede || ''} 
                onChange={(e) => setNovoCliente({...novoCliente, rede: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300 cursor-pointer"
              >
                <option value="">Selecione uma rede...</option>
                <option value="Rede Bem Viver">Rede Bem Viver</option>
                <option value="Rede Construnião">Rede Construnião</option>
                <option value="Rede Constru&Cia (Maringá)">Rede Constru&Cia (Maringá)</option>
                <option value="Rede Constru&Cia (Cascavel)">Rede Constru&Cia (Cascavel)</option>
                <option value="Rede Grandeoeste">Rede Grandeoeste</option>
              </select>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-blue-100">
              <label className="block text-xs font-bold mb-2 text-blue-600 uppercase tracking-wide">Constituição / Nascimento</label>
              <input 
                type="date" 
                value={novoCliente.data_constituicao_nascimento || ''} 
                onChange={(e) => setNovoCliente({...novoCliente, data_constituicao_nascimento: e.target.value})} 
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300" 
              />
            </div>
          </div>
          <div className="flex gap-4 mt-8">
            <button 
              onClick={salvarNovoCliente} 
              className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-10 py-4 rounded-xl flex items-center gap-3 font-bold shadow-lg hover:from-blue-600 hover:to-cyan-600 hover:scale-105 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group"
            >
              <Save size={22} className="group-hover:rotate-12 transition-transform duration-300" />
              Salvar Cliente
            </button>
            <button 
              onClick={() => {
                setMostrarCadastro(false);
                setNovoCliente({ nome: '', cnpj: '', endereco: '', bairro: '', cidade: '', estado: 'Parana', telefone: '', email: '', rede: '', data_constituicao_nascimento: '' });
                setErroValidacao({ cnpj: '', telefone: '' });
              }} 
              className="bg-gradient-to-r from-gray-400 to-gray-500 text-white px-10 py-4 rounded-xl flex items-center gap-3 font-bold shadow-lg hover:from-gray-500 hover:to-gray-600 hover:scale-105 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group"
            >
              <X size={22} className="group-hover:rotate-90 transition-transform duration-300" />
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de Clientes */}
      {loading ? (
        <Loading texto="Carregando clientes..." tamanho="md" />
      ) : clientesFiltrados.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <Users size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Nenhum cliente encontrado.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {clientesVisiveis.map((cliente) => (
            <div
              key={cliente.id}
              className="bg-white rounded-xl border-2 border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
            >
              {/* Header do Card */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setClienteExpandido(clienteExpandido === cliente.id ? null : cliente.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                      <Building2 size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">{cliente.nome}</h3>
                      <div className="flex items-center gap-1">
                        <p className="text-sm text-slate-500">{cliente.cnpj}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(cliente.cnpj || '');
                            setCnpjCopiado(cliente.id);
                            setTimeout(() => setCnpjCopiado(null), 2000);
                          }}
                          className="p-1 rounded-md hover:bg-blue-100 transition-all duration-200 group/copy"
                          title="Copiar CNPJ/CPF"
                        >
                          {cnpjCopiado === cliente.id ? (
                            <ClipboardCheck size={14} className="text-emerald-500" />
                          ) : (
                            <Copy size={14} className="text-slate-400 group-hover/copy:text-blue-500" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 flex-shrink-0">
                    {/* Situação */}
                    <div className="flex flex-col items-center w-[120px]">
                      <span className="text-xs text-slate-400 mb-1">Situação</span>
                      {getSituacaoBadge(cliente.status_calculado || cliente.status_cadastro)}
                    </div>

                    {/* Última Compra */}
                    <div className="flex flex-col items-center w-[80px]">
                      <span className="text-xs text-slate-400 mb-1">Últ. Compra</span>
                      <span className="text-xs font-medium text-slate-700 whitespace-nowrap">
                        {cliente.ultima_compra 
                          ? new Date(cliente.ultima_compra).toLocaleDateString('pt-BR')
                          : 'Sem compras'
                        }
                      </span>
                    </div>
                    
                    {/* Responsável Cliente */}
                    <div className="flex flex-col items-end w-[200px]">
                      <span className="text-xs text-slate-400 mb-1">Responsável</span>
                      <span className="text-sm font-medium text-blue-600 text-right" title={cliente.vendedor_responsavel || '-'}>
                        {obterNomeResponsavel(cliente.vendedor_responsavel)}
                      </span>
                    </div>
                    
                    {clienteExpandido === cliente.id ? (
                      <ChevronUp size={20} className="text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown size={20} className="text-slate-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>

              {/* Detalhes Expandidos */}
              {clienteExpandido === cliente.id && (
                <div className="px-4 pb-4 pt-2 border-t border-slate-200">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Endereço</p>
                        <p className="font-medium text-slate-700">
                          {cliente.endereco || 'Não informado'}
                          {cliente.bairro && `, ${cliente.bairro}`}
                        </p>
                        <p className="font-medium text-slate-700">
                          {cliente.cidade && `${cliente.cidade}`}
                          {cliente.estado && ` - ${cliente.estado}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Phone size={16} className="text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Telefone</p>
                        <p className="font-medium text-slate-700">{cliente.telefone || 'Não informado'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Building2 size={16} className="text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Rede</p>
                        <p className="font-medium text-slate-700">{cliente.rede || 'Não informado'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Mail size={16} className="text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500">E-mail</p>
                        <p className="font-medium text-slate-700">{cliente.email || 'Não informado'}</p>
                      </div>
                    </div>

                    {/* Constituição / Nascimento */}
                    <div className="flex items-start gap-2">
                      <Calendar size={16} className="text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Constituição / Nasc.</p>
                        <p className="font-medium text-slate-700">
                          {cliente.data_constituicao_nascimento 
                            ? new Date(cliente.data_constituicao_nascimento).toLocaleDateString('pt-BR')
                            : 'Não informado'}
                        </p>
                      </div>
                    </div>

                    {/* Porte e Limite - com Personalizado ao lado */}
                    <div className="flex items-start gap-2">
                      <Scale size={16} className="text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500">Porte / Limite</p>
                        <p className="font-medium text-slate-700">
                          {cliente.porte || 'Não definido'}
                          {cliente.limite_credito > 0 && (
                            <span className="text-emerald-600 ml-2">({formatarMoeda(cliente.limite_credito)})</span>
                          )}
                          {(() => {
                            const limitespadrao = { '0': 1000, '1': 5000, '2': 10000, '3': 20000 };
                            const limiteEsperado = limitespadrao[cliente.porte] || 0;
                            const temPersonalizado = cliente.limite_credito > 0 && cliente.limite_credito !== limiteEsperado;
                            return temPersonalizado ? (
                              <span className="ml-2 text-blue-600 font-semibold">• Personalizado</span>
                            ) : null;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Última Compra - Link para visualizar pedido */}
                  {cliente.ultima_compra && (
                    <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-emerald-600" />
                        <p className="text-sm text-emerald-700">
                          <strong>Última compra:</strong> {new Date(cliente.ultima_compra).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      {cliente.ultimo_pedido_id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Salvar o ID do pedido para abrir
                            localStorage.setItem('pedidoParaAbrir', String(cliente.ultimo_pedido_id));
                            // Disparar evento para navegar
                            window.dispatchEvent(new CustomEvent('navegarParaPedido', { 
                              detail: { pedidoId: String(cliente.ultimo_pedido_id) } 
                            }));
                          }}
                          className="text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all"
                        >
                          <Eye size={14} />
                          Visualizar pedido
                        </button>
                      )}
                    </div>
                  )}

                  {/* Observação */}
                  {editandoObservacao === cliente.id ? (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200" onClick={e => e.stopPropagation()}>
                      <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-2">Editar Observação</p>
                      <textarea
                        value={textoObservacao}
                        onChange={(e) => setTextoObservacao(e.target.value)}
                        rows={3}
                        className="w-full p-2 border-2 border-blue-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none resize-none"
                        placeholder="Digite a observação..."
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2 justify-end">
                        <button
                          onClick={(e) => cancelarEdicaoObservacao(e)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-all"
                        >
                          <X size={14} /> Cancelar
                        </button>
                        <button
                          onClick={(e) => salvarObservacao(cliente.id, e)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all"
                        >
                          <Save size={14} /> Salvar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4">
                      {(cliente.observacao_analise || podeEditarObservacao) && (
                        <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm flex-1" style={{ color: '#1F3D7A' }}>
                            {cliente.observacao_analise ? (
                              <><strong>Observação:</strong> {cliente.observacao_analise}</>
                            ) : (
                              <span className="text-slate-400 italic font-normal">Nenhuma observação cadastrada.</span>
                            )}
                          </p>
                          {podeEditarObservacao && (
                            <button
                              onClick={(e) => abrirEdicaoObservacao(cliente, e)}
                              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-blue-300 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg transition-all"
                            >
                              <Edit3 size={13} /> Editar Observação
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botões de Ação */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end gap-2">
                    {/* Se cliente está EXCLUIDO ou REJEITADO, mostra botão de Reativar (apenas para Diretoria/Administrativo/Admin) */}
                    {(cliente.status_cadastro === 'EXCLUIDO' || cliente.status_cadastro === 'REJEITADO') ? (
                      (usuario.cargo === 'Diretoria' || usuario.cargo === 'Administrativo' || usuario.cargo === 'Admin') && (
                        <button
                          onClick={(e) => reativarCliente(cliente, e)}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all text-sm"
                        >
                          <RefreshCw size={16} />
                          Reativar Cliente
                        </button>
                      )
                    ) : (
                      /* Para outros status, mostra botão de Editar */
                      <button
                        onClick={(e) => abrirEdicao(cliente, e)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all text-sm"
                      >
                        <Edit3 size={16} />
                        Editar Cliente
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Botão Ver Mais */}
          {temMaisClientes && (
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setQuantidadeVisivel(prev => prev + 10)}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-200 text-blue-600 font-semibold rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all"
              >
                <ChevronDown size={20} />
                Ver mais ({clientesFiltrados.length - quantidadeVisivel} restantes)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Linha divisória final */}
      <div className="w-full h-px bg-gray-200 mt-6"></div>

      {/* Modal de Edição */}
      {modalEdicao && clienteEditando && (
        <ModalEdicaoCliente
          cliente={clienteEditando}
          onClose={() => {
            setModalEdicao(false);
            setClienteEditando(null);
          }}
          onSalvar={salvarEdicao}
          podeEditarPorteLimite={podeEditarPorteLimite()}
          podeEditarNome={podeEditarNome()}
          podeExcluir={podeEditarPorteLimite()}
          onExcluir={async (motivo) => {
            try {
              await clienteAPI.excluirCliente(clienteEditando.id, {
                motivo,
                excluido_por: obterNomeUsuario(),
                usuario_id_excluiu: usuario?.id
              });
              showModal({
                type: 'success',
                title: 'Cliente Excluído',
                message: 'O cliente foi excluído com sucesso.',
                confirmText: 'OK',
                showCancel: false
              });
              setModalEdicao(false);
              setClienteEditando(null);
              carregarClientes();
            } catch (error) {
              showModal({
                type: 'error',
                title: 'Erro',
                message: 'Não foi possível excluir o cliente.',
                confirmText: 'OK',
                showCancel: false
              });
            }
          }}
        />
      )}
    </div>
  );
};

// ========================================
// MODAL DE EDIÇÃO DE CLIENTE
// ========================================
const ModalEdicaoCliente = ({ cliente, onClose, onSalvar, podeEditarPorteLimite, podeEditarNome, podeExcluir, onExcluir }) => {
  const { showModal } = useModal();
  const [form, setForm] = useState({
    nome: cliente.nome || '',
    endereco: cliente.endereco || '',
    bairro: cliente.bairro || '',
    cidade: cliente.cidade || '',
    estado: cliente.estado || '',
    telefone: cliente.telefone || '',
    email: cliente.email || '',
    rede: cliente.rede || '',
    vendedor_responsavel: cliente.vendedor_responsavel || '',
    vendedor_codigo: cliente.vendedor_codigo || null,
    porte: cliente.porte || '',
    limite_credito: cliente.limite_credito || 0,
    data_constituicao_nascimento: cliente.data_constituicao_nascimento ? cliente.data_constituicao_nascimento.substring(0, 10) : ''
  });
  const [salvando, setSalvando] = useState(false);
  const [mostrarConfirmacaoExclusao, setMostrarConfirmacaoExclusao] = useState(false);
  const [motivoExclusao, setMotivoExclusao] = useState('');
  const [limitePersonalizado, setLimitePersonalizado] = useState('');
  const [usuariosAtivos, setUsuariosAtivos] = useState([]);

  // Carregar usuários ativos para o select de responsável
  useEffect(() => {
    if (podeEditarPorteLimite) {
      const carregarUsuarios = async () => {
        try {
          const usuarios = await usuarioAPI.listarAtivos();
          setUsuariosAtivos(usuarios);
        } catch (error) {
          console.error('Erro ao carregar usuários:', error);
        }
      };
      carregarUsuarios();
    }
  }, [podeEditarPorteLimite]);

  const limites = {
    '0': 1000,
    '1': 5000,
    '2': 10000,
    '3': 20000
  };

  // Formatar valor digitado como moeda
  const formatarValorInput = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    const valorEmReais = parseFloat(apenasNumeros) / 100;
    return valorEmReais.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Converter valor formatado para número
  const converterParaNumero = (valorFormatado) => {
    if (!valorFormatado) return 0;
    return parseFloat(valorFormatado.replace(/\./g, '').replace(',', '.'));
  };

  // Handler para mudança do limite personalizado
  const handleLimiteChange = (valor) => {
    const valorFormatado = formatarValorInput(valor);
    setLimitePersonalizado(valorFormatado);
    // Atualizar o form com o valor convertido
    const valorNumerico = converterParaNumero(valorFormatado);
    setForm(prev => ({ ...prev, limite_credito: valorNumerico }));
  };

  const handleChange = (campo, valor) => {
    setForm(prev => ({ ...prev, [campo]: valor }));
    // Atualizar limite automaticamente ao mudar porte (apenas se não tiver limite personalizado)
    if (campo === 'porte' && limites[valor] && !limitePersonalizado) {
      setForm(prev => ({ ...prev, [campo]: valor, limite_credito: limites[valor] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Nome só é enviado por quem tem perfil de aprovação de cadastro
    const dados = { ...form };
    if (!podeEditarNome) {
      delete dados.nome;
    } else if (!dados.nome.trim()) {
      showModal({
        type: 'warning',
        title: 'Nome obrigatório',
        message: 'O nome do cliente não pode ficar em branco.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }

    setSalvando(true);
    await onSalvar(dados);
    setSalvando(false);
  };

  const handleExcluir = () => {
    if (!motivoExclusao.trim()) {
      showModal({
        type: 'warning',
        title: 'Motivo obrigatório',
        message: 'Informe o motivo da exclusão do cliente.',
        confirmText: 'OK',
        showCancel: false
      });
      return;
    }
    onExcluir(motivoExclusao);
  };

  const formatarMoeda = (valor) => {
    return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Status que permitem exclusão
  const podeSerExcluido = !['EM_ANALISE', 'PENDENTE', 'EXCLUIDO'].includes(cliente.status_cadastro);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden z-10 animate-scaleIn flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10"
          >
            <X size={24} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Edit3 size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Editar Cliente</h2>
              <p className="text-white/80 text-sm">{cliente.nome}</p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            {/* Nome - só editável por quem tem perfil de aprovação de cadastro */}
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Nome do Cliente
                {!podeEditarNome && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-slate-400">
                    <Lock size={12} />
                    Somente perfil de aprovação de cadastro
                  </span>
                )}
              </label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                disabled={!podeEditarNome}
                title={!podeEditarNome ? 'Apenas usuários com perfil de aprovação de cadastro podem alterar o nome' : undefined}
                className={`w-full px-4 py-2 border-2 rounded-xl focus:outline-none ${
                  podeEditarNome
                    ? 'border-slate-200 focus:border-blue-500'
                    : 'border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed'
                }`}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Endereço</label>
              <input
                type="text"
                value={form.endereco}
                onChange={(e) => handleChange('endereco', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Bairro</label>
              <input
                type="text"
                value={form.bairro}
                onChange={(e) => handleChange('bairro', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Cidade</label>
              <input
                type="text"
                value={form.cidade}
                onChange={(e) => handleChange('cidade', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Estado</label>
              <input
                type="text"
                value={form.estado}
                onChange={(e) => handleChange('estado', e.target.value.toUpperCase())}
                maxLength={2}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone</label>
              <input
                type="text"
                value={form.telefone}
                onChange={(e) => handleChange('telefone', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Rede</label>
              <select
                value={form.rede}
                onChange={(e) => handleChange('rede', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl bg-white focus:border-blue-500 focus:outline-none cursor-pointer"
              >
                <option value="">Selecione uma rede...</option>
                <option value="Rede Bem Viver">Rede Bem Viver</option>
                <option value="Rede Construnião">Rede Construnião</option>
                <option value="Rede Constru&Cia (Maringá)">Rede Constru&Cia (Maringá)</option>
                <option value="Rede Constru&Cia (Cascavel)">Rede Constru&Cia (Cascavel)</option>
                <option value="Rede Grandeoeste">Rede Grandeoeste</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Constituição / Nascimento</label>
              <input
                type="date"
                value={form.data_constituicao_nascimento || ''}
                onChange={(e) => handleChange('data_constituicao_nascimento', e.target.value)}
                className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Campo Responsável - só para admin/diretoria/administrativo */}
            {podeEditarPorteLimite && (
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Responsável</label>
                <select
                  value={form.vendedor_codigo !== null && form.vendedor_codigo !== undefined ? String(form.vendedor_codigo) : (form.vendedor_responsavel === null ? 'SEM_RESPONSAVEL' : '')}
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (valor === 'SEM_RESPONSAVEL') {
                      setForm(prev => ({ ...prev, vendedor_responsavel: null, vendedor_codigo: null }));
                    } else if (valor === '') {
                      setForm(prev => ({ ...prev, vendedor_responsavel: '', vendedor_codigo: null }));
                    } else {
                      // Encontrar o usuário selecionado pelo codigo_id
                      const usuarioSelecionado = usuariosAtivos.find(u => String(u.codigo_id) === valor);
                      if (usuarioSelecionado) {
                        setForm(prev => ({
                          ...prev,
                          vendedor_responsavel: usuarioSelecionado.username_ldap,
                          vendedor_codigo: usuarioSelecionado.codigo_id
                        }));
                      }
                    }
                  }}
                  className="w-full px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
                >
                  <option value="">Selecione um responsável</option>
                  <option value="SEM_RESPONSAVEL">Sem responsável</option>
                  {usuariosAtivos.map((u) => (
                    <option key={u.id} value={String(u.codigo_id)}>
                      {u.nome_completo} ({u.username_ldap})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Porte e Limite - só para admin/diretoria/administrativo */}
            {podeEditarPorteLimite && (
              <>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Porte do Cliente</label>
                  <div className="grid grid-cols-4 gap-3">
                    {['0', '1', '2', '3'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => handleChange('porte', p)}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          form.porte === p
                            ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-500/30'
                            : 'border-slate-200 bg-white hover:border-blue-300'
                        }`}
                      >
                        <p className="font-bold text-slate-800 text-sm">Porte {p}</p>
                        <p className="text-xs text-emerald-600 font-semibold mt-1">
                          Limite: {formatarMoeda(limites[p])}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Limite Personalizado */}
                {form.porte && (
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Limite Personalizado (opcional)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">R$</span>
                      <input
                        type="text"
                        value={limitePersonalizado}
                        onChange={(e) => handleLimiteChange(e.target.value)}
                        placeholder="0,00"
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {limitePersonalizado && converterParaNumero(limitePersonalizado) > 0 
                        ? <span className="text-blue-600 font-medium">Limite personalizado: {formatarMoeda(converterParaNumero(limitePersonalizado))}</span>
                        : `Se não informar, será usado o limite padrão do porte: ${formatarMoeda(limites[form.porte])}`
                      }
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Seção de Exclusão - apenas para admin/diretoria/administrativo */}
          {podeExcluir && podeSerExcluido && (
            <div className="mt-4 pt-4 border-t-2 border-red-100">
              {!mostrarConfirmacaoExclusao ? (
                <button
                  type="button"
                  onClick={() => setMostrarConfirmacaoExclusao(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 font-semibold rounded-xl hover:bg-red-100 transition-all border-2 border-red-200"
                >
                  <Ban size={20} />
                  Excluir Cliente
                </button>
              ) : (
                <div className="space-y-3 p-4 bg-red-50 rounded-xl border-2 border-red-200">
                  <div className="flex items-center gap-2 text-red-700 font-semibold">
                    <AlertTriangle size={20} />
                    Confirmar Exclusão
                  </div>
                  <p className="text-sm text-red-600">
                    Esta ação irá marcar o cliente como excluído. Ele não poderá mais fazer pedidos.
                  </p>
                  <textarea
                    value={motivoExclusao}
                    onChange={(e) => setMotivoExclusao(e.target.value)}
                    placeholder="Informe o motivo da exclusão (obrigatório)"
                    className="w-full px-3 py-2 border-2 border-red-200 rounded-lg focus:border-red-400 focus:outline-none resize-none"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setMostrarConfirmacaoExclusao(false);
                        setMotivoExclusao('');
                      }}
                      className="flex-1 px-3 py-2 bg-white text-slate-600 font-semibold rounded-lg hover:bg-slate-100 transition-all border border-slate-300"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleExcluir}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all"
                    >
                      <Ban size={16} />
                      Confirmar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
            >
              {salvando ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================================
// ABA: APROVAÇÕES DE CLIENTES
// ========================================
const AprovacoesClientes = () => {
  const { usuario } = useAuth();
  const { showModal } = useModal();
  const [clientesPendentes, setClientesPendentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [modalAprovacao, setModalAprovacao] = useState(false);
  const [processando, setProcessando] = useState(false);

  // Helper para obter nome formatado do usuário
  const obterNomeUsuario = () => {
    const username = usuario?.username_ldap || usuario?.nome || usuario?.username;
    if (username === 'admin_sistema') {
      return 'Administrador do Sistema';
    }
    return username || 'Usuário';
  };

  useEffect(() => {
    carregarClientesPendentes();
  }, []);

  const carregarClientesPendentes = async () => {
    try {
      setLoading(true);
      const data = await clienteAPI.listarPendentes();
      setClientesPendentes(data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes pendentes:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Não foi possível carregar os clientes pendentes.',
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const abrirModalAprovacao = (cliente) => {
    setClienteSelecionado(cliente);
    setModalAprovacao(true);
  };

  const handleAprovacao = async (aprovado, porte, observacao, limitePersonalizado = null) => {
    if (!clienteSelecionado) return;

    try {
      setProcessando(true);

      const limites = {
        '0': 1000,
        '1': 5000,
        '2': 10000,
        '3': 20000
      };

      // Usa limite personalizado se informado, senão usa o limite padrão do porte
      const limiteCredito = aprovado 
        ? (limitePersonalizado && limitePersonalizado > 0 ? limitePersonalizado : limites[porte])
        : 0;

      await clienteAPI.aprovarCliente(clienteSelecionado.id, {
        aprovado,
        porte: aprovado ? porte : null,
        limite_credito: limiteCredito,
        observacao_analise: observacao,
        aprovado_por: obterNomeUsuario(),
        usuario_id_aprovou: usuario?.id
      });

      showModal({
        type: 'success',
        title: aprovado ? 'Cliente Aprovado!' : 'Cadastro Rejeitado',
        message: aprovado 
          ? `O cliente ${clienteSelecionado.nome} foi aprovado com sucesso.`
          : `O cadastro de ${clienteSelecionado.nome} foi rejeitado.`,
        confirmText: 'OK',
        showCancel: false
      });

      setModalAprovacao(false);
      setClienteSelecionado(null);
      carregarClientesPendentes();

    } catch (error) {
      console.error('Erro ao processar aprovação:', error);
      showModal({
        type: 'error',
        title: 'Erro',
        message: 'Não foi possível processar a aprovação. Tente novamente.',
        confirmText: 'OK',
        showCancel: false
      });
    } finally {
      setProcessando(false);
    }
  };

  // Estatísticas
  const totalPendentes = clientesPendentes.length;

  // Formatar data e hora (sempre exibe no horário de Brasília)
  const formatarDataHora = (dataStr) => {
    if (!dataStr) return 'Data não disponível';
    const data = new Date(dataStr);
    const opcoes = { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
    const partes = new Intl.DateTimeFormat('pt-BR', opcoes).formatToParts(data);
    const v = {};
    partes.forEach(p => { v[p.type] = p.value; });
    return `${v.day}/${v.month}/${v.year} às ${v.hour}:${v.minute}`;
  };

  return (
    <div className="space-y-6">
      {/* Header simples estilo GerenciarUsuarios */}
      <div className="animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-default">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Aprovações de Clientes</h1>
              <p className="text-xs text-gray-400">Analise e aprove os cadastros de novos clientes</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold" style={{ color: '#2563eb' }}>{loading ? '...' : totalPendentes}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Aguardando Análise</p>
          </div>
        </div>
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200"></div>

      {/* Lista de Clientes Pendentes */}
      {loading ? (
        <Loading texto="Carregando clientes pendentes..." tamanho="md" />
      ) : clientesPendentes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-slate-100 shadow-sm">
          <CheckCircle size={64} className="mx-auto text-green-400 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhum pedido pendente!</h3>
          <p className="text-slate-500">Todos os pedidos foram analisados.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {clientesPendentes.map((cliente) => (
            <div
              key={cliente.id}
              className="bg-white rounded-xl border-2 border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-lg transition-all duration-300 p-5"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Info do Cliente */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                      <Building2 size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">{cliente.nome}</h3>
                      <p className="text-slate-500">{cliente.cnpj}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Cidade</p>
                      <p className="font-medium text-slate-700">{cliente.cidade || 'N/I'} - {cliente.estado || 'N/I'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Telefone</p>
                      <p className="font-medium text-slate-700">{cliente.telefone || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Rede</p>
                      <p className="font-medium text-slate-700">{cliente.rede || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">E-mail</p>
                      <p className="font-medium text-slate-700">{cliente.email || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Cadastrado por</p>
                      <p className="font-medium text-blue-600">{cliente.vendedor_responsavel || 'Sistema'}</p>
                    </div>
                  </div>

                  {cliente.endereco && (
                    <div className="mt-3 flex items-start gap-2 text-sm">
                      <MapPin size={16} className="text-slate-400 mt-0.5" />
                      <p className="text-slate-600">
                        {cliente.endereco}
                        {cliente.bairro && `, ${cliente.bairro}`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Botão de Análise */}
                <button
                  onClick={() => abrirModalAprovacao(cliente)}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all"
                >
                  <Eye size={20} />
                  Analisar
                </button>
              </div>

              {/* Data de cadastro */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
                <Calendar size={14} />
                Cadastrado em {formatarDataHora(cliente.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Linha divisória final */}
      <div className="w-full h-px bg-gray-200 mt-6"></div>

      {/* Modal de Aprovação */}
      {modalAprovacao && clienteSelecionado && (
        <ModalAprovacaoCliente
          cliente={clienteSelecionado}
          onClose={() => {
            setModalAprovacao(false);
            setClienteSelecionado(null);
          }}
          onAprovar={handleAprovacao}
          processando={processando}
        />
      )}
    </div>
  );
};

// ========================================
// MODAL DE APROVAÇÃO DE CLIENTE
// ========================================
const ModalAprovacaoCliente = ({ cliente, onClose, onAprovar, processando }) => {
  const [porte, setPorte] = useState('');
  const [observacao, setObservacao] = useState('');
  const [tipoAcao, setTipoAcao] = useState(null); // 'aprovar' ou 'rejeitar'
  const [limitePersonalizado, setLimitePersonalizado] = useState('');
  const [usarLimitePersonalizado, setUsarLimitePersonalizado] = useState(false);

  const limites = {
    '0': 1000,
    '1': 5000,
    '2': 10000,
    '3': 20000
  };

  const formatarMoeda = (valor) => {
    return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Formatar valor digitado como moeda
  const formatarValorInput = (valor) => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // Converte para centavos e depois para reais
    const valorEmReais = parseFloat(apenasNumeros) / 100;
    
    // Formata como moeda brasileira sem o símbolo
    return valorEmReais.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Converter valor formatado para número
  const converterParaNumero = (valorFormatado) => {
    if (!valorFormatado) return 0;
    // Remove pontos de milhar e substitui vírgula por ponto
    return parseFloat(valorFormatado.replace(/\./g, '').replace(',', '.'));
  };

  // Handler para mudança do limite personalizado
  const handleLimiteChange = (valor) => {
    const valorFormatado = formatarValorInput(valor);
    setLimitePersonalizado(valorFormatado);
  };

  const handleAprovar = () => {
    if (!porte) {
      return;
    }
    // Se tem limite personalizado válido, usa ele, senão usa o limite do porte
    const limiteValor = limitePersonalizado && converterParaNumero(limitePersonalizado) > 0
      ? converterParaNumero(limitePersonalizado)
      : null;
    onAprovar(true, porte, observacao, limiteValor);
  };

  const handleRejeitar = () => {
    if (!observacao.trim()) {
      return;
    }
    onAprovar(false, null, observacao, null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden z-10 animate-scaleIn flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10"
          >
            <X size={24} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Shield size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Análise de Cadastro</h2>
              <p className="text-white/80 text-sm">Revise os dados e aprove ou rejeite</p>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Dados do Cliente */}
          <div className="bg-slate-50 rounded-xl p-5 border-2 border-slate-100">
            <h3 className="font-bold text-slate-800 text-lg mb-4 flex items-center gap-2">
              <Building2 size={20} className="text-blue-500" />
              Dados do Cliente
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Nome / Razão Social</p>
                <p className="font-semibold text-slate-800">{cliente.nome}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">CNPJ/CPF</p>
                <p className="font-semibold text-slate-800">{cliente.cnpj}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Endereço</p>
                <p className="font-medium text-slate-700">
                  {cliente.endereco || 'Não informado'}
                  {cliente.bairro && `, ${cliente.bairro}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Cidade/Estado</p>
                <p className="font-medium text-slate-700">
                  {cliente.cidade || 'N/I'} - {cliente.estado || 'N/I'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Telefone</p>
                <p className="font-medium text-slate-700">{cliente.telefone || 'Não informado'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Rede</p>
                <p className="font-medium text-slate-700">{cliente.rede || 'Não informado'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">E-mail</p>
                <p className="font-medium text-slate-700">{cliente.email || 'Não informado'}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Vendedor Responsável</p>
              <p className="font-semibold text-blue-600">{cliente.vendedor_responsavel || 'Não identificado'}</p>
            </div>
          </div>

          {/* Seleção de Porte (obrigatório para aprovar) */}
          <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-100">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Scale size={20} className="text-blue-500" />
              Classificação do Cliente <span className="text-red-500">*</span>
            </h3>
            
            <div className="grid grid-cols-4 gap-3">
              {['0', '1', '2', '3'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPorte(p)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    porte === p
                      ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-500/30'
                      : 'border-slate-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <p className="font-bold text-slate-800">Porte {p}</p>
                  <p className="text-sm text-emerald-600 font-semibold mt-1">
                    Limite: {formatarMoeda(limites[p])}
                  </p>
                </button>
              ))}
            </div>
            
            {!porte && tipoAcao === 'aprovar' && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertTriangle size={14} />
                Selecione o porte do cliente para aprovar
              </p>
            )}

            {/* Limite Personalizado */}
            {porte && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Limite Personalizado (opcional)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">R$</span>
                  <input
                    type="text"
                    value={limitePersonalizado}
                    onChange={(e) => handleLimiteChange(e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {limitePersonalizado && converterParaNumero(limitePersonalizado) > 0 
                    ? <span className="text-blue-600 font-medium">Limite personalizado: {formatarMoeda(converterParaNumero(limitePersonalizado))}</span>
                    : `Se não informar, será usado o limite padrão do porte: ${formatarMoeda(limites[porte])}`
                  }
                </p>
              </div>
            )}
          </div>

          {/* Observação */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Observações {tipoAcao === 'rejeitar' && <span className="text-red-500">* (obrigatório para rejeição)</span>}
            </label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Adicione observações sobre a análise, motivo de rejeição, etc..."
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none"
              rows={3}
            />
            {!observacao.trim() && tipoAcao === 'rejeitar' && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertTriangle size={14} />
                Informe o motivo da rejeição
              </p>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={() => {
                setTipoAcao('rejeitar');
                if (observacao.trim()) {
                  handleRejeitar();
                }
              }}
              disabled={processando}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-100 text-red-700 font-semibold rounded-xl hover:bg-red-200 transition-all disabled:opacity-50"
            >
              {processando && tipoAcao === 'rejeitar' ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <XCircle size={20} />
              )}
              Rejeitar Cadastro
            </button>
            
            <button
              onClick={() => {
                setTipoAcao('aprovar');
                if (porte) {
                  handleAprovar();
                }
              }}
              disabled={processando}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
            >
              {processando && tipoAcao === 'aprovar' ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <CheckCircle size={20} />
              )}
              Aprovar Cliente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clientes;
